/**
 * Search History Service
 * Handles saving and retrieving search history
 */

class SearchHistoryService {
  constructor() {
    this.sessionId = null;
    this.initializeSession();
  }

  initializeSession() {
    if (typeof window !== "undefined") {
      this.sessionId = localStorage.getItem("searchSessionId");
      if (!this.sessionId) {
        this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem("searchSessionId", this.sessionId);
      }
    }
  }

  /**
   * Save a search to history
   * @param {Object} searchData - Search data to save
   * @param {string} searchData.searchQuery - The search query
   * @param {Object} searchData.filters - Applied filters
   * @param {number} searchData.resultsCount - Number of results found
   */
  async saveSearch(searchData) {
    try {
      const { searchQuery, filters = {}, resultsCount = 0 } = searchData;

      // Only save if we have a meaningful search query
      if (
        !searchQuery ||
        typeof searchQuery !== "string" ||
        searchQuery.trim().length === 0
      ) {
        console.log("Search history: Skipping save for empty search query");
        return {
          success: true,
          skipped: true,
          message: "Empty search query not saved",
        };
      }

      // Don't save very short queries (less than 2 characters)
      if (searchQuery.trim().length < 2) {
        console.log("Search history: Skipping save for short search query");
        return {
          success: true,
          skipped: true,
          message: "Short search query not saved",
        };
      }

      const response = await fetch("/api/search-history/save", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({
          searchQuery,
          filters,
          resultsCount,
          sessionId: this.sessionId,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to save search");
      }

      return data;
    } catch (error) {
      console.error("Error saving search history:", error);
      // Don't throw error - search history is not critical
      return { success: false, error: error.message };
    }
  }

  /**
   * Get recent searches for current user/session
   * @param {number} limit - Number of searches to retrieve
   */
  async getRecentSearches(limit = 5) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        ...(this.sessionId && { sessionId: this.sessionId }),
      });

      const response = await fetch(`/api/search-history/user?${params}`, {
        credentials: "include", // Include cookies for authentication
      });
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch searches");
      }

      return data.searches;
    } catch (error) {
      console.error("Error fetching search history:", error);
      return [];
    }
  }

  /**
   * Clear a specific search from history
   * @param {string} searchId - ID of search to remove
   */
  async clearSearch(searchId) {
    try {
      const response = await fetch("/api/search-history/clear", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({
          searchId,
          sessionId: this.sessionId,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to clear search");
      }

      return data;
    } catch (error) {
      console.error("Error clearing search:", error);
      throw error;
    }
  }

  /**
   * Clear all searches for current user/session
   */
  async clearAllSearches() {
    try {
      const response = await fetch("/api/search-history/clear", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({
          clearAll: true,
          sessionId: this.sessionId,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to clear search history");
      }

      return data;
    } catch (error) {
      console.error("Error clearing all searches:", error);
      throw error;
    }
  }

  /**
   * Convert search filters to URL query parameters
   * @param {Object} search - Search object with query and filters
   */
  searchToQueryParams(search) {
    const params = new URLSearchParams();

    if (search.query) {
      params.set("search", search.query);
    }

    Object.entries(search.filters || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params.set(key, value.toString());
      }
    });

    return params.toString();
  }

  /**
   * Create a readable description of search
   * @param {Object} search - Search object
   */
  createSearchDescription(search) {
    const { query, filters = {}, resultsCount } = search;
    let description = query;

    const filterParts = [];

    if (filters.type) {
      filterParts.push(filters.type);
    }

    if (filters.bedrooms) {
      filterParts.push(
        `${filters.bedrooms} bed${filters.bedrooms > 1 ? "s" : ""}`
      );
    }

    if (filters.city) {
      filterParts.push(`in ${filters.city}`);
    }

    if (filters.listingType) {
      filterParts.push(`for ${filters.listingType}`);
    }

    if (filters.minPrice || filters.maxPrice) {
      if (filters.minPrice && filters.maxPrice) {
        filterParts.push(
          `₦${filters.minPrice.toLocaleString()} - ₦${filters.maxPrice.toLocaleString()}`
        );
      } else if (filters.minPrice) {
        filterParts.push(`above ₦${filters.minPrice.toLocaleString()}`);
      } else if (filters.maxPrice) {
        filterParts.push(`under ₦${filters.maxPrice.toLocaleString()}`);
      }
    }

    if (filterParts.length > 0) {
      description += ` (${filterParts.join(", ")})`;
    }

    description += ` - ${resultsCount} result${resultsCount !== 1 ? "s" : ""}`;

    return description;
  }
}

// Create singleton instance
const searchHistoryService = new SearchHistoryService();

export default searchHistoryService;
